import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { RefreshCw, X, ClipboardList, Swords } from "lucide-react";

const MARKETS = [
  { code: "US", label: "United States" },
  { code: "CA", label: "Canada" },
];

const COMPETITOR_NAMES_BY_MARKET = {
  US: "This Old House, Bob Vila, Family Handyman, The Spruce, Angi",
  CA: "HomeStars, Reno-Assistance, Canadian Contractor, HGTV Canada, RONA",
};

function opportunityBadgeClass(score) {
  if (score >= 5000) return "bg-emerald-100 text-emerald-700 border-emerald-300";
  if (score >= 1000) return "bg-amber-100 text-amber-700 border-amber-300";
  return "bg-slate-100 text-slate-500 border-slate-300";
}

export default function AdminCompetitorGaps() {
  const [market, setMarket] = useState("US");
  const [gaps, setGaps] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);
  const navigate = useNavigate();

  const load = (m) => {
    setGaps(null);
    api.get(`/admin/newsroom/competitor-gaps?status=new&market=${m}`).then((res) => setGaps(res.data)).catch(() => {
      setGaps([]);
      toast.error("Failed to load competitor gaps");
    });
  };
  useEffect(() => { load(market); }, [market]);

  const refreshNow = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post(`/admin/newsroom/competitor-gaps/refresh?market=${market}`);
      if (data.throttled) {
        toast.info(data.message);
      } else {
        toast.success(data.new_gaps > 0 ? `Found ${data.new_gaps} new gap(s)` : "No new gaps right now");
      }
      load(market);
    } catch {
      toast.error("Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const dismiss = async (id) => {
    await api.post(`/admin/newsroom/competitor-gaps/${id}/dismiss`);
    setGaps((prev) => prev.filter((g) => g.id !== id));
    toast.success("Dismissed");
  };

  const generateBrief = async (gap) => {
    setGeneratingId(gap.id);
    try {
      await api.post("/admin/newsroom/briefs/generate", {
        source_type: "competitor_gap",
        source_id: gap.id,
        topic: gap.keyword,
        content_type: "evergreen_guide",
        market,
      });
      setGaps((prev) => prev.filter((g) => g.id !== gap.id));
      toast.success("Brief generated");
      navigate("/admin/briefs");
    } catch {
      toast.error("Brief generation failed");
    } finally {
      setGeneratingId(null);
    }
  };

  return (
    <div data-testid="admin-competitor-gaps-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-serif-heading font-bold text-navy">Competitor Gaps</h1>
          <p className="text-sm text-slate-500 mt-1">Keywords {COMPETITOR_NAMES_BY_MARKET[market]} rank for that HouseTalk Daily doesn't yet cover — real DataForSEO data, ranked by opportunity.</p>
        </div>
        <Button onClick={refreshNow} disabled={refreshing} data-testid="gaps-refresh-button" className="bg-gold text-navy hover:bg-gold/90">
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "Analyzing..." : `Refresh Now (${market})`}
        </Button>
      </div>

      <Tabs value={market} onValueChange={setMarket} className="mb-4">
        <TabsList data-testid="competitor-gaps-market-tabs">
          {MARKETS.map((m) => (
            <TabsTrigger key={m.code} value={m.code} data-testid={`competitor-gaps-market-tab-${m.code}`}>{m.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Opportunity</TableHead><TableHead>Keyword</TableHead>
              <TableHead>Search Volume</TableHead><TableHead>Competition</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {gaps?.map((g) => (
              <TableRow key={g.id} data-testid={`gap-item-${g.id}`}>
                <TableCell>
                  <Badge className={`border ${opportunityBadgeClass(g.opportunity_score)}`} data-testid={`gap-opportunity-${g.id}`}>
                    <Swords className="w-3 h-3 mr-1" /> {Math.round(g.opportunity_score).toLocaleString()}
                  </Badge>
                </TableCell>
                <TableCell className="font-medium text-navy capitalize">{g.keyword}</TableCell>
                <TableCell className="text-sm text-slate-600">{g.search_volume?.toLocaleString()}/mo</TableCell>
                <TableCell className="text-sm text-slate-500">{Math.round((g.competition ?? 0) * 100)}%</TableCell>
                <TableCell className="text-right space-x-2">
                  <Button size="sm" variant="outline" onClick={() => generateBrief(g)} disabled={generatingId === g.id} data-testid={`gap-brief-button-${g.id}`}>
                    <ClipboardList className="w-3.5 h-3.5 mr-1" /> {generatingId === g.id ? "Generating..." : "Generate Brief"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => dismiss(g.id)} data-testid={`gap-dismiss-button-${g.id}`}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {gaps?.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">No competitor gaps yet for {market}. Click "Refresh Now" to analyze competitors.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
